/**
 * Casi Salesforce (prova, QA26-F9.3): elenco dei casi 2026 copiati nello schema sf_staging, solo admin, sola lettura.
 * Dati di clienti: la pagina è oscurata nelle immagini dei feedback (data-sensitive) e non ha KPI propri.
 */
import { Input, Select, Space, Table, Tag } from "antd";
import { useState } from "react";
import { Link } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { FiltriSfCasi, SfCasoBreveDto, SfStatoDto } from "@qa/shared";
import { api } from "../../api/client";
import { fmtData } from "../../lib/format";
import { messaggioErrore } from "../../i18n/errori";
import { PageHead, Ro, Vuoto } from "../../components/ui";

/** Importo nella valuta del caso (anche INR e GBP, che il resto del portale non usa). */
export function fmtValuta(v: number | null, valuta: string | null) {
  if (v === null) return "—";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: valuta || "EUR" }).format(v);
  } catch {
    return `${v.toFixed(2)} ${valuta ?? ""}`.trim();
  }
}

export const coloreStato = (s: string | null) => (s === "CLOSED" ? "default" : s === "NEW" ? "blue" : s === "TO BE APPROVED" ? "gold" : "orange");

export default function SfCasiPage() {
  const { t } = useTranslation();
  const [f, setF] = useState<FiltriSfCasi>({});
  const stato = useQuery({ queryKey: ["sf", "stato"], queryFn: () => api.get<SfStatoDto>("/admin/sf/stato"), staleTime: 60_000 });
  const casi = useQuery({
    queryKey: ["sf", "casi", f],
    queryFn: () => api.get<SfCasoBreveDto[]>("/admin/sf/casi", f),
    enabled: stato.data?.attivo === true,
    placeholderData: keepPreviousData,
  });
  const st = stato.data;
  const head = (
    <PageHead
      crumbs={[{ label: t("portale.nome"), to: "/" }, { label: t("menu.gruppoAdmin") }, { label: t("menu.sfCasi") }]}
      title={t("admin.sf.titolo")}
      sub={t("admin.sf.sottotitolo")}
      extra={<Ro>{t("admin.sf.prova")}</Ro>}
    />
  );
  if (st && !st.attivo)
    return (
      <>
        {head}
        <Vuoto titolo={t("admin.sf.nonCaricati")} testo={t("admin.sf.nonCaricatiNota")} />
      </>
    );
  return (
    <>
      {head}
      <div data-sensitive>
        {st?.caricatoIl && (
          <p className="muted" style={{ marginTop: 0 }}>
            {t("admin.sf.caricatiIl", { data: fmtData(st.caricatoIl), casi: st.conteggi.casi, posizioni: st.conteggi.posizioni, storia: st.conteggi.storia })}
          </p>
        )}
        <Space wrap style={{ marginBottom: 14 }}>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={t("admin.sf.cliente")}
            aria-label={t("admin.sf.cliente")}
            style={{ width: 300 }}
            value={f.cliente}
            onChange={(v) => setF((x) => ({ ...x, cliente: v }))}
            options={(st?.clienti ?? []).map((o) => ({ value: o.value, label: `${o.label} · ${o.casi}` }))}
          />
          <Select
            allowClear
            placeholder={t("admin.sf.stato")}
            aria-label={t("admin.sf.stato")}
            style={{ width: 180 }}
            value={f.stato}
            onChange={(v) => setF((x) => ({ ...x, stato: v }))}
            options={(st?.stati ?? []).map((o) => ({ value: o.value, label: `${o.label} · ${o.casi}` }))}
          />
          <Select
            allowClear
            placeholder={t("admin.sf.recordType")}
            aria-label={t("admin.sf.recordType")}
            style={{ width: 300 }}
            value={f.recordType}
            onChange={(v) => setF((x) => ({ ...x, recordType: v }))}
            options={(st?.recordType ?? []).map((o) => ({ value: o.value, label: `${o.label} · ${o.casi}` }))}
          />
          <Input.Search allowClear placeholder={t("admin.sf.cerca")} aria-label={t("admin.sf.cerca")} style={{ width: 260 }} onSearch={(v) => setF((x) => ({ ...x, q: v.trim() || undefined }))} />
        </Space>
        <Table<SfCasoBreveDto>
          rowKey="id"
          size="middle"
          loading={stato.isFetching || casi.isFetching}
          dataSource={casi.data ?? []}
          pagination={{ pageSize: 50, hideOnSinglePage: true, showSizeChanger: false }}
          scroll={{ x: 1400 }}
          locale={{ emptyText: casi.isError ? messaggioErrore(casi.error) : stato.isError ? messaggioErrore(stato.error) : t("admin.sf.vuoto") }}
          columns={[
            { title: t("admin.sf.numero"), key: "n", width: 100, render: (_, c) => <Link className="mono" to={`/admin/sf/${c.id}`}>{c.numero}</Link> },
            { title: t("admin.sf.apertoIl"), key: "a", width: 110, render: (_, c) => <span className="num">{fmtData(c.apertoIl)}</span> },
            { title: t("admin.sf.stato"), key: "s", width: 130, render: (_, c) => <Tag color={coloreStato(c.stato)}>{c.stato ?? "—"}</Tag> },
            { title: t("admin.sf.recordType"), key: "r", width: 150, render: (_, c) => <span className="muted">{c.recordType ?? "—"}</span> },
            { title: t("admin.sf.account"), key: "cl", width: 200, render: (_, c) => c.account ?? "—" },
            { title: t("admin.sf.brand"), key: "b", width: 150, render: (_, c) => c.brand ?? "—" },
            { title: t("admin.sf.oggetto"), key: "o", width: 280, render: (_, c) => c.oggetto ?? "—" },
            { title: t("admin.sf.responsabilita"), key: "re", width: 120, render: (_, c) => c.responsabilita ?? "—" },
            { title: t("admin.sf.posizioni"), key: "p", width: 90, align: "right", render: (_, c) => <span className="num">{c.posizioni}</span> },
            {
              title: t("admin.sf.importoClaim"),
              key: "i",
              width: 130,
              align: "right",
              render: (_, c) => (
                <span className="num" data-sensitive>
                  {fmtValuta(c.importoClaim, c.valuta)}
                </span>
              ),
            },
          ]}
        />
      </div>
    </>
  );
}
